import {useContext, useState, useEffect} from "react";
import { UserContext } from '../Context/UserContext';
import {useNavigate} from "react-router-dom"
import HeaderTooltip from "./HeaderTooltip"
import "../styles/singleGame.css"

function SingleGameTable({relevantGame, ratingTimeframe}) {
    const {state, dispatch, isActiveSub} = useContext(UserContext);
    const navigate = useNavigate()
    const [dataTimeframe, setDataTimeframe] = useState("ThreeYear")
    const homeTeam = relevantGame?.home_team

    useEffect(() => {
        setDataTimeframe(ratingTimeframe ? "CurrentSeason" : "ThreeYear")
    }, [ratingTimeframe])

    const isUserGame = !!(relevantGame && relevantGame.calculation_status !== undefined);
    const dailyKey = isUserGame ? `userGameDaily${dataTimeframe}Stats` : `daily${dataTimeframe}Stats`
    const pitcherKey = isUserGame ? `userGameDailyPitcher${dataTimeframe}Stats` : `dailyPitcher${dataTimeframe}Stats`

    const dailyStats = state[dailyKey];
    const dailyPitcherStats = state[pitcherKey];

    const userGameDailyHitterStats = dailyStats?.filter((stat) => stat?.user_game_id === relevantGame?.id)
    const regGameDailyHitterStats = dailyStats?.filter((stat) => stat?.hitter_team === homeTeam || stat?.pitcher_team === homeTeam)

    const relevantHitterStats = isUserGame ? userGameDailyHitterStats : regGameDailyHitterStats

    const userGameDailyPitcherStats = dailyPitcherStats?.filter((stat) => stat.user_game_id === relevantGame?.id)
    const regGameDailyPitcherStats = dailyPitcherStats?.filter((stat) => stat.pitcher_team === homeTeam || stat.pitcher_team === relevantGame?.away_team)

    const relevantPitcherStats = isUserGame ? userGameDailyPitcherStats : regGameDailyPitcherStats

    const [relevantHitterState, setRelevantHitterState] = useState(relevantHitterStats ? relevantHitterStats.sort((a,b) => b.hit_matchup_rating - a.hit_matchup_rating) : [])
    const [relevantPitcherState, setRelevantPitcherState] = useState(relevantPitcherStats ? relevantPitcherStats : [])

    useEffect(() => {
        setRelevantHitterState(relevantHitterStats)
        setRelevantPitcherState(relevantPitcherStats)
    }, [homeTeam, ratingTimeframe, dailyStats, dailyPitcherStats])

    function fetchSingleHitterData(hitterID, isUserGame, relevantGameID) {
        fetch(`/hitters/${hitterID}?isUserGame=${isUserGame}&relevantGameID=${relevantGameID}`)
            .then(res => res.json())
            .then(data => {
                if (data) {
                    dispatch({type: "fetchSingleHitter", payload: data})
                }
                else {
                    console.error("Error: Hitter data not found")
                }
            })
    }

    function navigateToHitterPage(hitterName, hitterID) {
        state.singleHitter = []
        const formattedName = hitterName.replace(/ /g, "-")
        fetchSingleHitterData(hitterID, isUserGame, relevantGame.id)
        navigate(`/hitter/${formattedName}`, {
            state: {
                isUserGame: isUserGame,
                relevantGameID: relevantGame.id
            }
        })
        window.scrollTo(0, 0)
    }

    function navigateToPitcherPage(pitcherName, pitcherID) {
        state.singlePitcher = []
        const formattedName = pitcherName.replace(/ /g, "-")
        fetchSinglePitcherData(pitcherID, isUserGame, relevantGame.id)
        navigate(`/pitcher/${formattedName}`, {
            state: {
                isUserGame: isUserGame,
                relevantGameID: relevantGame.id
            }
        })
        window.scrollTo(0, 0)
    }

    function fetchSinglePitcherData(pitcherID, isUserGame, relevantGameID) {
        fetch(`/pitchers/${pitcherID}?isUserGame=${isUserGame}&relevantGameID=${relevantGameID}`)
            .then(res => res.json())
            .then(data => {
                if (data) {
                    dispatch({type: "fetchSinglePitcher", payload: data})
                }
                else {
                    console.error("Error: Pitcher data not found")
                }
            })
    }

    // Arsenal coverage cell — same logic as HitterRatingsTable
    function renderArsenalCell(stat) {
        const stars = stat.pitch_overall_stars
        const pct   = stat.pitch_overall_arsenal_pct

        if (stars == null && pct == null) {
            return <td className="arsenal-coverage-cell arsenal-no-report">—</td>
        }

        return (
            <td className="arsenal-coverage-cell">
                <span className="arsenal-stars">
                    {Array.from({ length: 5 }, (_, i) => (
                        <span key={i} className={i < (stars ?? 0) ? "star filled" : "star empty"}>★</span>
                    ))}
                </span>
                {stat.pitch_low_data ? (
                    <span
                        className="arsenal-no-pitch-data"
                        title={`Only ${stat.pitch_data_coverage_pct?.toFixed(0)}% of arsenal has enough PA`}
                    >
                        No Pitch Data
                    </span>
                ) : (
                    <span className="arsenal-pct-label">
                        {pct != null ? `${Number(pct).toFixed(1)}%` : "—"}
                    </span>
                )}
            </td>
        )
    }

    const hitterColSpan = isActiveSub ? "19" : "15"

    if (isActiveSub && relevantPitcherState.length > 0)
    return (
        <div className="ratings-container">
            <div className="singleGamePitcherTableContainer">
                <table>
                    <thead>
                        <tr>
                            <th><HeaderTooltip label="Name" /></th>
                            <th><HeaderTooltip label="Team" /></th>
                            <th><HeaderTooltip label="Throw" tooltipKey="Throw Hand" /></th>
                            <th><HeaderTooltip label="Matchup Rating" tooltipKey="Pitcher Matchup Rating" /></th>
                            <th><HeaderTooltip label="IP" /></th>
                            <th><HeaderTooltip label="Avg Matchup Rtg" tooltipKey="Avg Hitter Matchup Rating" /></th>
                            <th><HeaderTooltip label="Avg wOBA" tooltipKey="Avg Hitter Matchup wOBA" /></th>
                            <th><HeaderTooltip label="Avg ISO" tooltipKey="Avg Hitter Matchup ISO" /></th>
                            <th><HeaderTooltip label="Avg Hard %" tooltipKey="Avg Hitter Matchup Hard Contact" /></th>
                            <th><HeaderTooltip label="Avg K %" tooltipKey="Avg Hitter Matchup K Pct" /></th>
                            <th><HeaderTooltip label="Avg GB %" tooltipKey="Avg Hitter Matchup GB Pct" /></th>
                            <th><HeaderTooltip label="Elite Hitters" tooltipKey="Number of Elite Rated Hitters" /></th>
                            <th><HeaderTooltip label="Poor Hitters" tooltipKey="Number of Poor Rated Hitters" /></th>
                            <th><HeaderTooltip label="High K % Hitters" tooltipKey="Number Hitters Above MLB Avg K Pct" /></th>
                            <th><HeaderTooltip label="High GB % Hitters" tooltipKey="Number Hitters Above MLB Avg GB Pct" /></th>
                        </tr>
                    </thead>
                    <tbody>
                        {relevantPitcherState?.map((stat) => {
                            const teamStyles = {backgroundColor: state.teamColors[stat.pitcher_team].primary};
                            const strikeoutRateStyles = {
                                backgroundColor: (stat.avg_hitter_matchup_k_pct >= 30.0 ? 'lightblue' : '') || (stat.avg_hitter_matchup_k_pct >= 26.0 && stat.avg_hitter_matchup_k_pct < 30.0 ? 'lightgreen' : '') || (stat.avg_hitter_matchup_k_pct > 23.0 && stat.avg_hitter_matchup_k_pct < 26.0 ? 'lightyellow' : '') || (stat.avg_hitter_matchup_k_pct <= 20.0 ? 'lightpink' : '')
                            }
                            const groundballRateStyles = {
                                backgroundColor: (stat.avg_hitter_matchup_gb_pct >= 50.0 ? 'lightblue' : '') || (stat.avg_hitter_matchup_gb_pct >= 46.0 && stat.avg_hitter_matchup_gb_pct < 50.0 ? 'lightgreen' : '') || (stat.avg_hitter_matchup_gb_pct > 43.0 && stat.avg_hitter_matchup_gb_pct < 46.0 ? 'lightyellow' : '') || (stat.avg_hitter_matchup_gb_pct <= 37.0 ? 'lightpink' : '')
                            }
                            const hardContactStyles = {
                                backgroundColor: (stat.avg_hitter_matchup_hard_contact <= 26.0 ? 'lightblue' : '') || (stat.avg_hitter_matchup_hard_contact >= 26.01 && stat.avg_hitter_matchup_hard_contact < 29.0 ? 'lightgreen' : '') || (stat.avg_hitter_matchup_hard_contact >= 29.01 && stat.avg_hitter_matchup_hard_contact < 32.0 ? 'lightyellow' : '') || (stat.avg_hitter_matchup_hard_contact >= 36.5 ? 'lightpink' : '')
                            }
                            const isoStyles = {
                                backgroundColor: (stat.avg_hitter_matchup_iso <= 0.129 ? 'lightblue' : '') || (stat.avg_hitter_matchup_iso >= 0.130 && stat.avg_hitter_matchup_iso < 0.140 ? 'lightgreen' : '') || (stat.avg_hitter_matchup_iso >= 0.140 && stat.avg_hitter_matchup_iso < 0.150 ? 'lightyellow' : '') || (stat.avg_hitter_matchup_iso >= 0.175 ? 'lightpink' : '')
                            }
                            const wobaStyles = {
                                backgroundColor: (stat.avg_hitter_matchup_woba <= 0.280 ? 'lightblue' : '') || (stat.avg_hitter_matchup_woba > 0.280 && stat.avg_hitter_matchup_woba < 0.300 ? 'lightgreen' : '') || (stat.avg_hitter_matchup_woba >= 0.300 && stat.avg_hitter_matchup_woba < 0.330 ? 'lightyellow' : '') || (stat.avg_hitter_matchup_woba >= 0.350 ? 'lightpink' : '')
                            }
                            const pitcherMatchupRatingStyles = {
                                backgroundColor: (stat.pitcher_matchup_rating_thirty_percent <= 5.63 ? 'lightblue' : '') || (stat.pitcher_matchup_rating_thirty_percent > 5.63 && stat.pitcher_matchup_rating_thirty_percent <= 5.85 ? 'lightgreen' : '') || (stat.pitcher_matchup_rating_thirty_percent > 5.85 && stat.pitcher_matchup_rating_thirty_percent < 6.05 ? 'lightyellow' : '') || (stat.pitcher_matchup_rating_thirty_percent >= 6.4 ? 'lightpink' : '')
                            }
                            return (
                                <tr key={stat.id}>
                                    <td id="pitcherNameTD" onClick={() => navigateToPitcherPage(stat?.pitcher_name, stat?.pitcher_id)} >{stat?.pitcher_name}</td>
                                    <td style={teamStyles}>{stat?.pitcher_team}</td>
                                    <td>{stat?.throw_hand}</td>
                                    <td style={pitcherMatchupRatingStyles}>{stat?.pitcher_matchup_rating_thirty_percent}</td>
                                    <td>{stat?.pitcher_ip}</td>
                                    <td>{stat?.avg_hitter_matchup_rating}</td>
                                    <td style={wobaStyles}>{stat?.avg_hitter_matchup_woba}</td>
                                    <td style={isoStyles}>{stat?.avg_hitter_matchup_iso}</td>
                                    <td style={hardContactStyles}>{stat?.avg_hitter_matchup_hard_contact}%</td>
                                    <td style={strikeoutRateStyles}>{stat?.avg_hitter_matchup_k_pct}%</td>
                                    <td style={groundballRateStyles}>{stat?.avg_hitter_matchup_gb_pct}%</td>
                                    <td>{stat?.num_elite_rated_hitters}</td>
                                    <td>{stat?.num_poor_rated_hitters}</td>
                                    <td>{stat?.num_hitters_above_avg_k_pct}</td>
                                    <td>{stat?.num_hitters_above_avg_gb_pct}</td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            </div>
            <br />
            <div className="singleGameHitterTableContainer">
                <table className={`singleGameHitterTable ${isActiveSub ? 'sub-active' : 'sub-inactive'}`}>
                    <thead>
                        <tr className="parentHeaders">
                            <th colSpan="3" className="hitterInfoGroup">Hitter Info</th>
                            <th colSpan={hitterColSpan} className="hitterStatsGroup">Hitter Profile & Matchup Stats</th>
                            <th colSpan="14" className="pitcherGroup">Pitcher Info & Stats</th>
                        </tr>
                        <tr className="regularHeaders">
                            <th><HeaderTooltip label="Name" /></th>
                            <th><HeaderTooltip label="Team" /></th>
                            <th><HeaderTooltip label="Bat" tooltipKey="Bat Hand" /></th>
                            <th><HeaderTooltip label="Matchup Rating" tooltipKey="Hitter Matchup Rating" /></th>
                            <th><HeaderTooltip label="Arsenal Coverage" tooltipKey="Pitch Arsenal Coverage" /></th>
                            <th><HeaderTooltip label="PA" /></th>
                            <th><HeaderTooltip label="WRC+" /></th>
                            <th><HeaderTooltip label="wOBA" /></th>
                            <th><HeaderTooltip label="Matchup wOBA" /></th>
                            <th><HeaderTooltip label="wOBA % Chg" tooltipKey="wOBA Pct Change" /></th>
                            <th><HeaderTooltip label="ISO" /></th>
                            <th><HeaderTooltip label="Matchup ISO" /></th>
                            <th><HeaderTooltip label="ISO % Chg" tooltipKey="ISO Pct Change" /></th>
                            <th><HeaderTooltip label="Hard %" tooltipKey="Hard Contact" /></th>
                            <th><HeaderTooltip label="Matchup Hard %" tooltipKey="Matchup Hard Contact" /></th>
                            <th><HeaderTooltip label="Hard % Chg" tooltipKey="Hard Contact Pct Change" /></th>
                            <th><HeaderTooltip label="HR/FB" /></th>
                            <th><HeaderTooltip label="Matchup HR/FB" /></th>
                            <th><HeaderTooltip label="HR/FB % Chg" tooltipKey="HR/FB Pct Change" /></th>
                            <th><HeaderTooltip label="FB %" tooltipKey="FB Pct" /></th>
                            <th><HeaderTooltip label="Matchup FB %" tooltipKey="Matchup FB Pct" /></th>
                            <th><HeaderTooltip label="FB % Chg" tooltipKey="FB Pct Change" /></th>
                            <th>SP Name</th>
                            <th>SP Team</th>
                            <th><HeaderTooltip label="SP Throw" tooltipKey="Throw Hand" /></th>
                            <th><HeaderTooltip label="IP" /></th>
                            <th>SP wOBA</th>
                            <th>SP ISO</th>
                            <th><HeaderTooltip label="SP Hard %" tooltipKey="SP Hard Contact Rate" /></th>
                            <th><HeaderTooltip label="SP Soft %" tooltipKey="Soft Contact Rate" /></th>
                            <th><HeaderTooltip label="SP K %" tooltipKey="K Rate" /></th>
                            <th><HeaderTooltip label="SP BB %" tooltipKey="Walk Rate" /></th>
                            <th><HeaderTooltip label="xFIP" /></th>
                            <th><HeaderTooltip label="SP FB %" tooltipKey="FB Rate" /></th>
                            <th><HeaderTooltip label="SP GB %" tooltipKey="GB Rate" /></th>
                            <th>SP HR/FB</th>
                        </tr>
                    </thead>
                    <tbody>
                        {relevantHitterState?.map((stat) => {
                            const teamStyles = {backgroundColor: state.teamColors[stat.hitter_team].primary};
                            const matchupRatingStyles = {
                                backgroundColor: (stat.hit_matchup_rating >= 0.450 ? 'lightblue' : '') || (stat.hit_matchup_rating >= 0.400 && stat.hit_matchup_rating < 0.450 ? 'lightgreen' : '') || (stat.hit_matchup_rating > 0.333 && stat.hit_matchup_rating < 0.400 ? 'lightyellow' : '') || (stat.hit_matchup_rating <= 0.230 ? 'lightpink' : '')
                            }
                            const matchupWOBAStyles = {
                                backgroundColor: (stat.hit_matchup_woba >= 0.400 ? 'lightblue' : '') || (stat.hit_matchup_woba < 0.400 && stat.hit_matchup_woba > 0.350 ? 'lightgreen' : '') || (stat.hit_matchup_woba >= 0.315 && stat.hit_matchup_woba < 0.350 ? 'lightyellow' : '') || (stat.hit_matchup_woba <= 0.300 ? 'lightpink' : '')
                            }
                            const matchupISOStyles = {
                                backgroundColor: (stat.hit_matchup_iso >= 0.275 ? 'lightblue' : '') || (stat.hit_matchup_iso < 0.275 && stat.hit_matchup_iso >= 0.200 ? 'lightgreen' : '') || (stat.hit_matchup_iso > 0.160 && stat.hit_matchup_iso < 0.199 ? 'lightyellow' : '') || (stat.hit_matchup_iso <= 0.150 ? 'lightpink' : '')
                            }
                            const matchupHCStyles = {
                                backgroundColor: (stat.hit_matchup_hard_contact >= 50.0 ? 'lightblue' : '') || (stat.hit_matchup_hard_contact < 50.0 && stat.hit_matchup_hard_contact >= 40.0 ? 'lightgreen' : '') || (stat.hit_matchup_hard_contact >= 33.0 && stat.hit_matchup_hard_contact < 40.0 ? 'lightyellow' : '') || (stat.hit_matchup_hard_contact <= 29.0 ? 'lightpink' : '')
                            }
                            const matchupFBStyles = {
                                backgroundColor: (stat.hit_matchup_fb_pct >= 50.0 ? 'lightblue' : '') || (stat.hit_matchup_fb_pct < 50.0 && stat.hit_matchup_fb_pct >= 40.0 ? 'lightgreen' : '') || (stat.hit_matchup_fb_pct > 37.0 && stat.hit_matchup_fb_pct < 40.0 ? 'lightyellow' : '') || (stat.hit_matchup_fb_pct <= 30.0 ? 'lightpink' : '')
                            }
                            const wOBApercentChangeStyles = {
                                color: (stat.hit_woba_percent_change > 0 ? 'rgba(0, 100, 0)' : 'rgba(139, 0, 0)')
                            }
                            const ISOpercentChangeStyles = {
                                color: (stat.hit_iso_percent_change > 0 ? 'rgba(0, 100, 0)' : 'rgba(139, 0, 0)')
                            }
                            const HCpercentChangeStyles = {
                                color: (stat.hit_hard_contact_percent_change > 0 ? 'rgba(0, 100, 0)' : 'rgba(139, 0, 0)')
                            }
                            const HRFBpercentChangeStyles = {
                                color: (stat.hit_hr_per_fb_percent_change > 0 ? 'rgba(0, 100, 0)' : 'rgba(139, 0, 0)')
                            }
                            const FBpercentChangeStyles = {
                                color: (stat.hit_fb_percent_change > 0 ? 'rgba(0, 100, 0)' : 'rgba(139, 0, 0)')
                            }
                            return (
                                <tr key={stat.id}>
                                    <td id="hitterNameTD" onClick={() => navigateToHitterPage(stat?.hitter_name, stat?.hitter_id)}>{stat?.hitter_name}</td>
                                    <td style={teamStyles}>{stat?.hitter_team}</td>
                                    <td>{stat?.bat_hand}</td>
                                    <td style={matchupRatingStyles}>{stat?.hit_matchup_rating?.toFixed(3)}</td>
                                    {renderArsenalCell(stat)}
                                    <td>{stat?.hit_pa}</td>
                                    <td>{stat?.hit_wrc_plus}</td>
                                    <td>{stat?.hit_woba}</td>
                                    <td style={matchupWOBAStyles}>{stat?.hit_matchup_woba?.toFixed(3)}</td>
                                    <td style={wOBApercentChangeStyles}>{stat?.hit_woba_percent_change?.toFixed(1)}%</td>
                                    <td>{stat?.hit_iso}</td>
                                    <td style={matchupISOStyles}>{stat?.hit_matchup_iso?.toFixed(3)}</td>
                                    <td style={ISOpercentChangeStyles}>{stat?.hit_iso_percent_change?.toFixed(1)}%</td>
                                    <td>{stat?.hit_hard_contact_pct}%</td>
                                    <td style={matchupHCStyles}>{stat?.hit_matchup_hard_contact?.toFixed(1)}%</td>
                                    <td style={HCpercentChangeStyles}>{stat?.hit_hard_contact_percent_change?.toFixed(1)}%</td>
                                    <td>{stat?.hit_hr_per_fb}%</td>
                                    <td>{stat?.hit_matchup_hr_per_fb?.toFixed(1)}%</td>
                                    <td style={HRFBpercentChangeStyles}>{stat?.hit_hr_per_fb_percent_change?.toFixed(1)}%</td>
                                    <td>{stat?.hit_fb_pct}%</td>
                                    <td style={matchupFBStyles}>{stat?.hit_matchup_fb_pct?.toFixed(1)}%</td>
                                    <td style={FBpercentChangeStyles}>{stat?.hit_fb_percent_change?.toFixed(1)}%</td>
                                    <td>{stat?.pitcher_name}</td>
                                    <td>{stat?.pitcher_team}</td>
                                    <td>{stat?.throw_hand}</td>
                                    <td>{stat?.sp_ip}</td>
                                    <td>{stat?.sp_woba}</td>
                                    <td>{stat?.sp_iso}</td>
                                    <td>{stat?.sp_hard_contact.toFixed(1)}%</td>
                                    <td>{stat?.sp_soft_contact.toFixed(1)}%</td>
                                    <td>{stat?.sp_kpct.toFixed(1)}%</td>
                                    <td>{stat?.sp_bbpct.toFixed(1)}%</td>
                                    <td>{stat?.sp_xfip}</td>
                                    <td>{stat?.sp_fbpct.toFixed(1)}%</td>
                                    <td>{stat?.sp_gbpct.toFixed(1)}%</td>
                                    <td>{stat?.sp_hr_per_fb.toFixed(1)}%</td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    )
    else return (
        <div className="ratings-container">
            <div className="singleGameHitterTableContainer">
                <table className="singleGameHitterTable sub-inactive">
                    <thead>
                        <tr className="parentHeaders">
                            <th colSpan="3" className="hitterInfoGroup">Hitter Info</th>
                            <th colSpan="14" className="hitterStatsGroup">Hitter Profile & Matchup Stats</th>
                            <th colSpan="14" className="pitcherGroup">Pitcher Info & Stats</th>
                        </tr>
                        <tr className="regularHeadersNoSub">
                            <th><HeaderTooltip label="Name" /></th>
                            <th><HeaderTooltip label="Team" /></th>
                            <th><HeaderTooltip label="Bat" tooltipKey="Bat Hand" /></th>
                            <th><HeaderTooltip label="Matchup Rating" tooltipKey="Hitter Matchup Rating" /></th>
                            <th><HeaderTooltip label="Arsenal Coverage" tooltipKey="Pitch Arsenal Coverage" /></th>
                            <th><HeaderTooltip label="PA" /></th>
                            <th><HeaderTooltip label="WRC+" /></th>
                            <th><HeaderTooltip label="wOBA" /></th>
                            <th><HeaderTooltip label="Matchup wOBA" /></th>
                            <th><HeaderTooltip label="ISO" /></th>
                            <th><HeaderTooltip label="Matchup ISO" /></th>
                            <th><HeaderTooltip label="Hard %" tooltipKey="Hard Contact" /></th>
                            <th><HeaderTooltip label="Matchup Hard %" tooltipKey="Matchup Hard Contact" /></th>
                            <th><HeaderTooltip label="HR/FB" /></th>
                            <th><HeaderTooltip label="Matchup HR/FB" /></th>
                            <th><HeaderTooltip label="FB %" tooltipKey="FB Pct" /></th>
                            <th><HeaderTooltip label="Matchup FB %" tooltipKey="Matchup FB Pct" /></th>
                            <th>SP Name</th>
                            <th>SP Team</th>
                            <th><HeaderTooltip label="SP Throw" tooltipKey="Throw Hand" /></th>
                            <th><HeaderTooltip label="IP" /></th>
                            <th>SP wOBA</th>
                            <th>SP ISO</th>
                            <th><HeaderTooltip label="SP Hard %" tooltipKey="SP Hard Contact Rate" /></th>
                            <th><HeaderTooltip label="SP Soft %" tooltipKey="Soft Contact Rate" /></th>
                            <th><HeaderTooltip label="SP K %" tooltipKey="K Rate" /></th>
                            <th><HeaderTooltip label="SP BB %" tooltipKey="Walk Rate" /></th>
                            <th><HeaderTooltip label="xFIP" /></th>
                            <th><HeaderTooltip label="SP FB %" tooltipKey="FB Rate" /></th>
                            <th><HeaderTooltip label="SP GB %" tooltipKey="GB Rate" /></th>
                            <th>SP HR/FB</th>
                        </tr>
                    </thead>
                    <tbody>
                        {relevantHitterState?.map((stat) => {
                            const teamStyles = {backgroundColor: state.teamColors[stat.hitter_team].primary}
                            const matchupRatingStyles = {
                                backgroundColor: (stat.hit_matchup_rating >= 0.450 ? 'lightblue' : '') || (stat.hit_matchup_rating >= 0.400 && stat.hit_matchup_rating < 0.450 ? 'lightgreen' : '') || (stat.hit_matchup_rating > 0.333 && stat.hit_matchup_rating < 0.400 ? 'lightyellow' : '') || (stat.hit_matchup_rating <= 0.230 ? 'lightpink' : '')
                            }
                            const matchupWOBAStyles = {
                                backgroundColor: (stat.hit_matchup_woba >= 0.400 ? 'lightblue' : '') || (stat.hit_matchup_woba < 0.400 && stat.hit_matchup_woba > 0.350 ? 'lightgreen' : '') || (stat.hit_matchup_woba >= 0.315 && stat.hit_matchup_woba < 0.350 ? 'lightyellow' : '') || (stat.hit_matchup_woba <= 0.300 ? 'lightpink' : '')
                            }
                            const matchupISOStyles = {
                                backgroundColor: (stat.hit_matchup_iso >= 0.275 ? 'lightblue' : '') || (stat.hit_matchup_iso < 0.275 && stat.hit_matchup_iso >= 0.200 ? 'lightgreen' : '') || (stat.hit_matchup_iso > 0.160 && stat.hit_matchup_iso < 0.199 ? 'lightyellow' : '') || (stat.hit_matchup_iso <= 0.150 ? 'lightpink' : '')
                            }
                            const matchupHCStyles = {
                                backgroundColor: (stat.hit_matchup_hard_contact >= 50.0 ? 'lightblue' : '') || (stat.hit_matchup_hard_contact < 50.0 && stat.hit_matchup_hard_contact >= 40.0 ? 'lightgreen' : '') || (stat.hit_matchup_hard_contact >= 33.0 && stat.hit_matchup_hard_contact < 40.0 ? 'lightyellow' : '') || (stat.hit_matchup_hard_contact <= 29.0 ? 'lightpink' : '')
                            }
                            const matchupFBStyles = {
                                backgroundColor: (stat.hit_matchup_fb_pct >= 50.0 ? 'lightblue' : '') || (stat.hit_matchup_fb_pct < 50.0 && stat.hit_matchup_fb_pct >= 40.0 ? 'lightgreen' : '') || (stat.hit_matchup_fb_pct > 37.0 && stat.hit_matchup_fb_pct < 40.0 ? 'lightyellow' : '') || (stat.hit_matchup_fb_pct <= 30.0 ? 'lightpink' : '')
                            }
                            return (
                                <tr key={stat.id}>
                                    <td onClick={() => navigateToHitterPage(stat?.hitter_name, stat?.hitter_id)}>{stat?.hitter_name}</td>
                                    <td style={teamStyles}>{stat?.hitter_team}</td>
                                    <td>{stat?.bat_hand}</td>
                                    <td style={matchupRatingStyles}>{stat?.hit_matchup_rating?.toFixed(3)}</td>
                                    {renderArsenalCell(stat)}
                                    <td>{stat?.hit_pa}</td>
                                    <td>{stat?.hit_wrc_plus}</td>
                                    <td>{stat?.hit_woba}</td>
                                    <td style={matchupWOBAStyles}>{stat?.hit_matchup_woba?.toFixed(3)}</td>
                                    <td>{stat?.hit_iso}</td>
                                    <td style={matchupISOStyles}>{stat?.hit_matchup_iso?.toFixed(3)}</td>
                                    <td>{stat?.hit_hard_contact_pct.toFixed(2)}%</td>
                                    <td style={matchupHCStyles}>{stat?.hit_matchup_hard_contact?.toFixed(2)}%</td>
                                    <td>{stat?.hit_hr_per_fb.toFixed(2)}%</td>
                                    <td>{stat?.hit_matchup_hr_per_fb?.toFixed(2)}%</td>
                                    <td>{stat?.hit_fb_pct.toFixed(2)}%</td>
                                    <td style={matchupFBStyles}>{stat?.hit_matchup_fb_pct?.toFixed(2)}%</td>
                                    <td>{stat?.pitcher_name}</td>
                                    <td>{stat?.pitcher_team}</td>
                                    <td>{stat?.throw_hand}</td>
                                    <td>{stat?.sp_ip}</td>
                                    <td>{stat?.sp_woba}</td>
                                    <td>{stat?.sp_iso}</td>
                                    <td>{stat?.sp_hard_contact.toFixed(2)}%</td>
                                    <td>{stat?.sp_soft_contact.toFixed(2)}%</td>
                                    <td>{stat?.sp_kpct.toFixed(2)}%</td>
                                    <td>{stat?.sp_bbpct.toFixed(2)}%</td>
                                    <td>{stat?.sp_xfip}</td>
                                    <td>{stat?.sp_fbpct.toFixed(2)}%</td>
                                    <td>{stat?.sp_gbpct.toFixed(2)}%</td>
                                    <td>{stat?.sp_hr_per_fb.toFixed(2)}%</td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    )
}

export default SingleGameTable